'use client';

import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { RESULT_ROWS } from './data';

const HATCH =
  'bg-[repeating-linear-gradient(45deg,var(--color-muted-foreground),var(--color-muted-foreground)_4px,transparent_4px,transparent_8px)]';

/**
 * The results as a live comparison, not a table: numeric rows draw paired
 * before/after bars (scaled within the row — the numbers, not bar lengths,
 * carry cross-row comparison), qualitative rows read as statements. Bars
 * animate once when the section scrolls into view.
 */
export function ResultsPanel() {
  const reduceMotion = useReducedMotion();
  const [played, setPlayed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setPlayed(true);
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const show = played || !!reduceMotion;

  return (
    <div ref={ref} className="px-4 pb-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-foreground py-2 font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        <span>Metric</span>
        <span className="flex items-center gap-4 normal-case">
          <span className="inline-flex items-center gap-1.5">
            <i
              aria-hidden
              className={cn('h-2 w-3.5 border border-edge', HATCH)}
            />
            before
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i
              aria-hidden
              className="h-2 w-3.5 border border-edge bg-foreground"
            />
            after
          </span>
        </span>
      </div>

      {RESULT_ROWS.map((row, index) => {
        const startsGroup =
          index > 0 && row.group !== RESULT_ROWS[index - 1].group;
        const hasBars = row.beforeN != null && row.afterN != null;

        return (
          <div
            key={row.metric}
            className={cn(
              'grid grid-cols-1 items-center gap-x-6 gap-y-2 border-b border-edge py-3.5 last:border-b-0 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]',
              startsGroup && 'border-t border-t-foreground'
            )}
          >
            <div>
              <p className="text-sm font-medium">{row.metric}</p>
              <p className="text-xs text-muted-foreground">{row.sub}</p>
            </div>

            {hasBars ? (
              <div className="flex flex-col gap-1.5">
                {(
                  [
                    ['before', row.before, HATCH, 100],
                    [
                      'after',
                      row.after,
                      'bg-foreground',
                      // biome-ignore lint/style/noNonNullAssertion: hasBars guards both
                      (row.afterN! / row.beforeN!) * 100,
                    ],
                  ] as const
                ).map(([tone, valueText, fillClass, percent], barIndex) => (
                  <div
                    key={tone}
                    className="grid grid-cols-[minmax(0,1fr)_5.5rem] items-center gap-3"
                  >
                    <div aria-hidden className="h-2 overflow-hidden bg-muted">
                      <motion.div
                        initial={reduceMotion ? false : { width: '0%' }}
                        animate={{ width: show ? `${percent}%` : '0%' }}
                        transition={
                          reduceMotion
                            ? { duration: 0 }
                            : {
                                duration: 0.9,
                                ease: [0.22, 1, 0.36, 1],
                                delay: barIndex * 0.05,
                              }
                        }
                        // Inline width keeps the bar truthful before
                        // hydration; the 2px floor keeps an extreme shrink
                        // (840s → 66s) visible instead of vanishing.
                        style={{ width: `${percent}%`, minWidth: 2 }}
                        className={cn('h-full', fillClass)}
                      />
                    </div>
                    <span
                      className={cn(
                        'text-right font-mono text-[11px] whitespace-nowrap tabular-nums',
                        tone === 'before'
                          ? 'text-muted-foreground'
                          : 'font-medium text-foreground'
                      )}
                    >
                      {valueText}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-mono text-xs tabular-nums">
                <span className="text-muted-foreground">{row.before}</span>
                <span aria-hidden className="mx-2 text-muted-foreground">
                  →
                </span>
                <span className="font-medium text-foreground">{row.after}</span>
              </p>
            )}

            <p className="font-mono text-xs font-medium text-foreground tabular-nums sm:text-right">
              {row.change}
            </p>
          </div>
        );
      })}
    </div>
  );
}
